import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createCaptureQueue } from '../data/queue.js'
import { loadDay, updateDay, linkRecordId, closeAt } from '../data/day.js'
import { todayISO } from '../data/workdate.js'
import './workday.css'

import backChevron from '../assets/figma/workday/back-chevron.svg'
import dotActive from '../assets/figma/workday/dot-active.svg'
import dotCheckedOut from '../assets/figma/workday/dot-checkedout.svg'
import dotNotActive from '../assets/figma/home/status-dot-notactive.svg'
import markerCheckIn from '../assets/figma/workday/marker-checkin.svg'
import markerCheckOutPending from '../assets/figma/workday/marker-checkout.svg'
import markerCheckOutDone from '../assets/figma/workday/marker-checkout-done.svg'
import dialogQuestion from '../assets/figma/workday/dialog-question.svg'

/*
 * Workday — implemented from the approved Figma prototype, frames
 *   4:2   3 — Workday / Full Screen         → the working state
 *   4:52  State A — Check-Out Confirmation  → the scrim + dialog
 *   9:77  (the checked-out card from 9:58)  → the checked-out state
 *
 * Structure, spacing, type and colour are the Figma values — see workday.css.
 *
 * THE TIMELINE RENDERS RECORDED EVENTS ONLY. A check-out that has not happened
 * shows Figma's "--:-- --" placeholder (4:46) in Figma's muted #8b97a6; no time
 * is ever inferred or manufactured. This is the Independent Facts Principle
 * applied to the worker's own screen.
 *
 * NO AUTOMATIC CHECK-OUT. The only thing that ends a workday is this screen's
 * CHECK OUT button, confirmed through the State A dialog.
 */
export default function Workday({ adapter, session, onHome, onCheckInAgain }) {
  const [day, setDay] = useState(() => loadDay())
  const [workday, setWorkday] = useState(null)   // { status, segments, minutes, ... }
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const closeFiredRef = useRef(false)

  const daySession = day?.session ?? session
  const employeeId = daySession?.employee?.id ?? null

  const queue = useMemo(
    () => createCaptureQueue(adapter, {
      onCheckinSynced: (captureId, recordId) => { linkRecordId(captureId, recordId); setDay(loadDay()) },
    }),
    [adapter],
  )
  useEffect(() => () => queue.dispose(), [queue])

  // U-4: an unmatched person has no employeeId, so the typed name is what
  // identifies their day. It is part of the lookup, so it is part of the deps.
  const typedName = daySession?.employee?.name ?? null

  const refresh = useCallback(() => {
    if (typeof adapter.getWorkdayForDate !== 'function') return
    adapter.getWorkdayForDate(employeeId, todayISO(), typedName)
      .then(setWorkday)
      .catch(() => { /* the device record below still renders the screen */ })
  }, [adapter, employeeId, typedName])

  useEffect(() => { refresh() }, [refresh])

  /* FD-001 automatic close of the REPORTING WINDOW — preserved exactly as the
   * day screen implemented it. This closes the reporting window on an already
   * checked-out day; it is NOT an automatic check-out and never ends a
   * working day. */
  useEffect(() => {
    if (day?.status !== 'checked-out' || !day) return
    const at = closeAt(day)
    if (!at) return
    const fire = () => { void doDayClose() }
    const remaining = at - Date.now()
    if (remaining <= 0) { fire(); return }
    const t = setTimeout(fire, remaining)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day?.status, day?.checkedOutAt])

  async function doDayClose() {
    if (closeFiredRef.current) return
    closeFiredRef.current = true
    const rec = loadDay()
    if (!rec || rec.status !== 'checked-out') return
    const now = new Date().toISOString()
    if (!rec.held) {
      await queue.captureDayClose(
        rec.session, now,
        { recordId: rec.recordId, checkedInAt: rec.checkedInAt, held: rec.held },
        rec.captureId,
      )
    }
    setDay(updateDay({ status: 'closed' }))
  }

  async function doCheckOut() {
    if (busy || !day) return
    setBusy(true)
    const now = new Date().toISOString()
    const entry = { recordId: day.recordId, checkedInAt: day.checkedInAt, held: day.held }
    const res = await queue.captureCheckOut(daySession, now, entry, day.captureId)
    // Administratively Voided server-side: converge quietly to closed. An
    // ordinary lifecycle action never overrides a governed disposition.
    if (res.voided) {
      setDay(updateDay({ status: 'closed' }))
    } else {
      setDay(updateDay({ status: 'checked-out', checkedOutAt: now }))
    }
    setConfirming(false)
    setBusy(false)
    refresh()
  }

  // Segments are the shared record of the day (every check-in and check-out,
  // in order). The device record is the fallback when the adapter has none.
  const segments = workday?.segments?.length
    ? workday.segments
    : (day?.checkedInAt
        ? [{ checkedInAt: day.checkedInAt, checkedOutAt: day.checkedOutAt ?? null,
             reportedProjectName: daySession?.project?.name ?? null, held: day.held }]
        : [])

  const open = segments.find((s) => !s.checkedOutAt) ?? null
  const working = !!open
  const last = segments[segments.length - 1] ?? null
  const cardProject = (open ?? last)?.reportedProjectName ?? daySession?.project?.name ?? null
  const heldAny = segments.some((s) => s.held)

  return (
    <div className="wd" data-figma="4:2">
      <div className="wd-header">
        <button type="button" className="wd-back" onClick={onHome}>
          <img className="wd-back__icon" src={backChevron} alt="" />
          Home
        </button>
        <p className="wd-title">WORKDAY</p>
        <div className="wd-header__spacer" />
      </div>

      <div className="wd-body">
        {working ? (
          /* ACTIVE card — 4:21 */
          <div className="wd-card" data-figma="4:21">
            <div className="wd-card__label">
              <img className="wd-card__dot" src={dotActive} alt="" />
              <p className="wd-card__status">ACTIVE</p>
            </div>
            <p className="wd-card__line">{cardProject ?? 'Project not yet matched'}</p>
            <p className="wd-card__line">Since {timeLabel(open.checkedInAt)}</p>
          </div>
        ) : segments.length > 0 ? (
          /* Checked-out card — 9:77 */
          <div className="wd-card wd-card--out" data-figma="9:77">
            <div className="wd-card__label">
              <img className="wd-card__dot" src={dotCheckedOut} alt="" />
              <p className="wd-card__status">CHECKED OUT</p>
            </div>
            <p className="wd-card__line">{cardProject ?? 'Project not yet matched'}</p>
            <p className="wd-card__line wd-card__line--muted">
              {timeLabel(segments[0].checkedInAt)} &ndash; {timeLabel(last.checkedOutAt)}
            </p>
          </div>
        ) : (
          /* NEW / NOT YET FIGMA-APPROVED — the initial Workday state. Figma has
             no "not checked in" Workday frame; this is the smallest extension
             in the existing visual language (Employee Home 3:41's NOT ACTIVE
             tokens). Nothing around it is altered to accommodate it. */
          <div className="wd-card wd-card--notactive" data-figma="none — Build 001 extension">
            <div className="wd-card__label">
              <img className="wd-card__dot" src={dotNotActive} alt="" />
              <p className="wd-card__status">NOT ACTIVE</p>
            </div>
            <p className="wd-card__line">You are not checked in.</p>
          </div>
        )}

        <div className="wd-pad-26" />
        <p className="wd-today" data-figma="4:27">TODAY</p>
        <div className="wd-pad-14" />

        <div className="wd-timeline" data-figma="4:30">
          {segments.length === 0 ? (
            <div className="wd-event">
              <img className="wd-event__marker" src={markerCheckOutPending} alt="" />
              <div className="wd-event__text">
                <p className="wd-event__label">Check In</p>
                <p className="wd-event__time wd-event__time--pending">--:-- --</p>
              </div>
            </div>
          ) : segments.map((seg, i) => (
            <div key={i} style={{ width: '100%' }}>
              {i > 0 && (
                <div className="wd-connector"><div className="wd-connector__rule" /></div>
              )}
              <div className="wd-event" data-figma="4:31">
                <img className="wd-event__marker" src={markerCheckIn} alt="" />
                <div className="wd-event__text">
                  <p className="wd-event__label">Check In</p>
                  <p className="wd-event__time">{timeLabel(seg.checkedInAt)}</p>
                </div>
              </div>
              <div className="wd-connector" data-figma="4:38">
                <div className="wd-connector__rule" />
              </div>
              <div className="wd-event" data-figma="4:40">
                <img
                  className="wd-event__marker"
                  src={seg.checkedOutAt ? markerCheckOutDone : markerCheckOutPending}
                  alt=""
                />
                <div className="wd-event__text">
                  <p className="wd-event__label">Check Out</p>
                  {seg.checkedOutAt ? (
                    <p className="wd-event__time">{timeLabel(seg.checkedOutAt)}</p>
                  ) : (
                    /* 4:46 — the event has not happened. Never inferred. */
                    <p className="wd-event__time wd-event__time--pending">--:-- --</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="wd-grow" />

        {heldAny && (
          <>
            <p className="wd-note" role="status">
              This check-in is held for the office to match up. Your time is recorded.
            </p>
            <div className="wd-pad-14" />
          </>
        )}

        {working ? (
          <button type="button" className="wd-cta" data-figma="4:48"
                  disabled={busy} onClick={() => setConfirming(true)}>
            CHECK OUT
          </button>
        ) : segments.length > 0 && onCheckInAgain ? (
          /* NEW / NOT YET FIGMA-APPROVED affordance — it opens State B (9:58),
             which IS approved. Same button geometry as 4:48. */
          <button type="button" className="wd-cta wd-cta--secondary"
                  disabled={busy} onClick={onCheckInAgain}>
            CHECK IN AGAIN
          </button>
        ) : null}
      </div>

      {/* State A — Check-Out Confirmation (4:52). No automatic check-out ever;
          this dialog is the only way a workday ends. */}
      {confirming && (
        <>
          <button type="button" className="wd-scrim" aria-label="Cancel"
                  data-figma="4:102" onClick={() => !busy && setConfirming(false)} />
          <div className="wd-dialog" role="dialog" aria-modal="true"
               aria-labelledby="wd-dialog-title" data-figma="4:103">
            <img className="wd-dialog__icon" src={dialogQuestion} alt="" />
            <p className="wd-dialog__title" id="wd-dialog-title">
              Are you<br />checking out?
            </p>
            <button type="button" className="wd-dialog__yes" disabled={busy} onClick={doCheckOut}>
              {busy ? 'Checking out…' : 'Yes, Check Out'}
            </button>
            <button type="button" className="wd-dialog__cancel" disabled={busy}
                    onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function timeLabel(iso) {
  if (!iso) return '--:-- --'
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) }
  catch { return '--:-- --' }
}
